// src/components/admin/Orders.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaEye, FaSearch, FaSync, FaShoppingBag, FaClock, FaCalendarDay, FaMoneyBillWave, FaWhatsapp, FaPrint, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './Orders.css';
import { printInvoice } from '../../utils/printInvoice';

const STATUS_OPTIONS = ['pending', 'confirmed', 'Processing', 'Shipped', 'delivered', 'Returned', 'cancelled'];
const PAGE_SIZE = 10;

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total_orders: 0, total_revenue: 0, pending_count: 0, today_count: 0 });
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  const [viewOrder, setViewOrder] = useState(null);

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [paymentFilter, setPaymentFilter] = useState('all');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMatching, setTotalMatching] = useState(0);

  const [selectedOrderNumber, setSelectedOrderNumber] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    fetchOrders(currentPage, searchTerm, statusFilter, fromDate, toDate);
    // eslint-disable-next-line
  }, [currentPage]);

  // Reset to page 1 whenever the search/status filter changes, then fetch
  useEffect(() => {
    setCurrentPage(1);
    fetchOrders(1, searchTerm, statusFilter, fromDate, toDate, paymentFilter);
  }, [searchTerm, statusFilter, fromDate, toDate, paymentFilter]);

  const fetchOrders = async (page, search, status, from, to, payment) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE, search, status });
      if (from) params.append('from_date', from);
      if (to) params.append('to_date', to);
      if (payment && payment !== 'all') params.append('payment', payment);
      const response = await fetch(`https://demo-site.creativepulse.pk/demo-api/admin/get-all-orders.php?${params}`);
      const data = await response.json();
      if (data.success) {
        setOrders(data.orders || []);
        setStats(data.stats || stats);
        setTotalPages(data.pagination.total_pages);
        setTotalMatching(data.pagination.total);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-confirmed',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      Returned: 'status-returned',
      cancelled: 'status-cancelled'
    };
    return statusClasses[status] || 'status-default';
  };

  const statCards = [
    { title: 'Total Orders', value: stats.total_orders, sub: 'All-time orders', icon: <FaShoppingBag />, bg: '#fdece8', color: '#e8604c' },
    { title: 'Pending Orders', value: stats.pending_count, sub: 'Need confirmation', icon: <FaClock />, bg: '#fdf1d6', color: '#a3760a' },
    { title: "Today's Orders", value: stats.today_count, sub: 'New orders today', icon: <FaCalendarDay />, bg: '#dbeafe', color: '#1d4ed8' },
    { title: 'Revenue This Month', value: 'PKR ' + Number(stats.total_revenue).toLocaleString(), sub: 'Confirmed sales', icon: <FaMoneyBillWave />, bg: '#dcf5e3', color: '#1e8a4c' }
  ];

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPaymentFilter('all');
    setFromDate('');
    setToDate('');
  };

  const openOrder = async (orderNumber) => {
    setSelectedOrderNumber(orderNumber);
    setDetailLoading(true);
    setOrderDetail(null);
    try {
      const res = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/get-order-detail.php?order_number=' + encodeURIComponent(orderNumber));
      const data = await res.json();
      if (data.success) {
        setOrderDetail(data.order);
        setEditStatus(data.order.status || 'pending');
        setEditNotes(data.order.internal_notes || '');
      }
    } catch (e) {
      console.error('Error fetching order detail:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeOrder = () => {
    setSelectedOrderNumber(null);
    setOrderDetail(null);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      const res = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/update-order.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_number: selectedOrderNumber, status: editStatus, internal_notes: editNotes })
      });
      const data = await res.json();
      if (data.success) {
        setOrderDetail(function (prev) {
          return Object.assign({}, prev, { status: editStatus, internal_notes: editNotes });
        });
        fetchOrders(currentPage, searchTerm, statusFilter, fromDate, toDate, paymentFilter);
      } else {
        alert(data.message || 'Failed to save');
      }
    } catch (e) {
      alert('Connection error');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => window.print();

  const itemsSubtotal = orderDetail && orderDetail.items
    ? orderDetail.items.reduce(function (sum, i) { return sum + parseFloat(i.price) * i.quantity; }, 0)
    : 0;

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="orders-page">
      <div className="orders-header">
        <h1>Orders</h1>
        <p>Manage customer orders, update statuses, and keep track of deliveries.</p>
      </div>

      <div className="orders-stats-grid">
        {statCards.map((stat, index) => (
          <div key={index} className="orders-stat-card">
            <div className="orders-stat-icon" style={{ background: stat.bg, color: stat.color }}>
              {stat.icon}
            </div>
            <p className="orders-stat-title">{stat.title}</p>
            <h3 className="orders-stat-value">{stat.value}</h3>
            <p className="orders-stat-sub">{stat.sub}</p>
          </div>
        ))}
      </div>

      <div className="orders-filter-bar">
        <div className="orders-search">
          <FaSearch className="orders-search-icon" />
          <input
            type="text"
            placeholder="Search by order ID, customer name, or phone number"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="orders-filter-select">
          <option value="all">All Statuses</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>

        <select
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
          className="orders-filter-select"
        >
          <option value="all">All Payments</option>
          <option value="cod">Cash On Delivery</option>
          <option value="bank_transfer">Bank Transfer</option>
        </select>

        <div className="orders-date-range">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="orders-filter-select"
            max={toDate || undefined}
          />
          <span className="orders-date-sep">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="orders-filter-select"
            min={fromDate || undefined}
          />
        </div>

        <button className="reset-filters-btn" onClick={handleResetFilters}>
          <FaSync /> Reset Filters
        </button>
      </div>

      {loading ? (
        <div className="loader-container">
          <div className="loader"></div>
          <p>Loading orders...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="no-orders">
          <p>No orders found.</p>
        </div>
      ) : (
        <>
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td className="order-id-cell">{order.order_number}</td>
                    <td>{order.customer_name}</td>
                    <td>{order.customer_phone || '—'}</td>
                    <td>{new Date(order.created_at).toLocaleDateString()}</td>
                    <td>{'PKR ' + parseFloat(order.total_amount).toLocaleString()}</td>
                    <td>{order.payment_method || '—'}</td>
                    <td>
                      <span className={'status-pill ' + getStatusBadgeClass(order.status)}>
                        {order.status || 'pending'}
                      </span>
                    </td>
                    <td>
                      <button className="view-order-btn" onClick={() => openOrder(order.order_number)}>
                        <FaEye /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div className="orders-pagination">
            <span className="orders-pagination-info">
              {'Showing ' + ((currentPage - 1) * PAGE_SIZE + 1) + '–' + Math.min(currentPage * PAGE_SIZE, totalMatching) + ' of ' + totalMatching + ' orders'}
            </span>
            <div className="orders-pagination-controls">
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                <FaChevronLeft />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((p, idx, arr) => (
                  <React.Fragment key={p}>
                    {idx > 0 && arr[idx - 1] !== p - 1 && <span className="orders-pagination-ellipsis">…</span>}
                    <button
                      className={p === currentPage ? 'orders-pagination-page active' : 'orders-pagination-page'}
                      onClick={() => goToPage(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                <FaChevronRight />
              </button>
            </div>
          </div>
        </>
      )}

      {selectedOrderNumber && (
        <div className="order-drawer-overlay">
          <div className="order-drawer-backdrop" onClick={closeOrder}></div>
          <div className="order-drawer">
            {detailLoading || !orderDetail ? (
              <div className="order-drawer-loading">Loading order...</div>
            ) : (
              <React.Fragment>
                <div className="order-drawer-header">
                  <div>
                    <h2>{orderDetail.order_number}</h2>
                    <span className={'status-pill ' + getStatusBadgeClass(orderDetail.status)}>
                      {'Status: ' + (orderDetail.status || 'pending')}
                    </span>
                  </div>
                  <button className="order-drawer-close" onClick={closeOrder}>×</button>
                </div>

                <div className="order-drawer-body">
                  <section className="order-section">
                    <h3>Customer Details</h3>
                    <div className="order-customer-grid">
                      <div><span>Name</span>{orderDetail.customer_name}</div>
                      <div><span>Phone</span>{orderDetail.customer_phone || '—'}</div>
                      <div className="order-full-row"><span>Email</span>{orderDetail.customer_email}</div>
                      <div className="order-full-row">
                        <span>Delivery Address</span>
                        {orderDetail.address + (orderDetail.city ? ', ' + orderDetail.city : '')}
                      </div>
                    </div>
                  </section>

                  <section className="order-section">
                    <h3>Order Items</h3>
                    {orderDetail.items && orderDetail.items.length > 0 ? (
                      orderDetail.items.map((item, idx) => (
                        <div key={item.id != null ? item.id : idx} className="order-item-row">
                          <img src={item.image || 'https://via.placeholder.com/50'} alt={item.name} />
                          <div className="order-item-info">
                            <strong>{item.name}</strong>
                            <span className="order-item-qty">
                              {'Qty: ' + item.quantity + ' × PKR ' + parseFloat(item.price).toLocaleString()}
                            </span>
                          </div>
                          <span className="order-item-total">
                            {'PKR ' + (item.price * item.quantity).toLocaleString()}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="order-muted">No items recorded for this order.</p>
                    )}
                  </section>

                  <section className="order-section">
                    <h3>Payment Summary</h3>
                    <div className="order-summary-rows">
                      <div>
                        <span>Subtotal</span>
                        <strong>{'PKR ' + (orderDetail.subtotal_amount != null ? parseFloat(orderDetail.subtotal_amount).toLocaleString() : itemsSubtotal.toLocaleString())}</strong>
                      </div>
                      <div>
                        <span>Shipping</span>
                        <strong>{Number(orderDetail.shipping_amount) > 0 ? 'PKR ' + parseFloat(orderDetail.shipping_amount).toLocaleString() : 'Free'}</strong>
                      </div>
                      {Number(orderDetail.flat_discount_amount) > 0 && (
                        <div>
                          <span>Flat Discount</span>
                          <strong>{'- PKR ' + parseFloat(orderDetail.flat_discount_amount).toLocaleString()}</strong>
                        </div>
                      )}
                      {Number(orderDetail.promo_discount_amount) > 0 && (
                        <div>
                          <span>Promo Discount</span>
                          <strong>{'- PKR ' + parseFloat(orderDetail.promo_discount_amount).toLocaleString()}</strong>
                        </div>
                      )}
                      <div>
                        <span>Total Amount</span>
                        <strong>{'PKR ' + parseFloat(orderDetail.total_amount).toLocaleString()}</strong>
                      </div>
                      <div>
                        <span>Payment Method</span>
                        <strong style={{ textTransform: 'uppercase' }}>{orderDetail.payment_method || '—'}</strong>
                      </div>
                    </div>
                  </section>

                  <section className="order-section">
                    <h3>Update Order Status</h3>
                    <div className="order-status-row">
                      <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                      <button className="order-update-btn" onClick={handleSaveOrder} disabled={saving}>
                        {saving ? 'Saving...' : 'Update Status'}
                      </button>
                    </div>
                  </section>

                  <section className="order-section">
                    <h3>Internal Notes</h3>
                    <p className="order-muted" style={{ marginTop: 0 }}>
                      Private — visible to your team only, not shown to the customer.
                    </p>
                    <textarea
                      rows="3"
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Add private notes for your team..."
                    />
                  </section>
                </div>

                <div className="order-drawer-footer">

                  <a className="order-whatsapp-btn"
                    href={'https://wa.me/' + (orderDetail.customer_phone || '').replace(/\D/g, '')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaWhatsapp /> Contact on WhatsApp
                  </a>
                  {/* 
                  <button className="order-print-btn" onClick={handlePrint}>
                    <FaPrint /> Print Invoice
                  </button>
                  */}
                  <button className="order-print-btn" onClick={() => printInvoice(orderDetail)}>Print Invoice</button>
                  <button className="order-close-btn" onClick={closeOrder}>Close</button>
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;